import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingCancellationReason } from 'webapp/enums/enums';
import { shuffleArray } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import PopupInnerModalComponent, { type Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface MainChoice extends LabelValuePairNumeric {
	value: BillingCancellationReason;
}

export default class AccountBillingCancellationFlowPopupFeedbackComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('request') declare requestService: Service.Request;

	@tracked mainChoiceSelectedValue = 0;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const feedback = this.flowCancellationService.getFeedback();
		if (feedback?.choice) {
			const selectedChoice = this.mainChoices.find((item) => item.label === feedback.choice);
			if (selectedChoice) {
				this.mainChoiceSelectedValue = selectedChoice.value;
			}
		}
	}

	mainChoices: MainChoice[] = shuffleArray([
		{
			value: BillingCancellationReason.AI_TOOLS,
			label: 'We use AI for analysis and reporting',
			label2: '(E.g. Claude, ChatGPT, Gemini)',
		},
		{
			value: BillingCancellationReason.OWN_AI_TOOL,
			label: 'We built our own reporting tool using AI',
			label2: '(E.g. Lovable, Replit, Base44)',
		},
		{
			value: BillingCancellationReason.BI_COMPETITOR,
			label: 'We moved to a more advanced BI/Data competitor',
			label2: '(E.g. Tableau, Looker, PowerBI, Domo)',
		},
		{
			value: BillingCancellationReason.BUILT_IN_ANALYTICS,
			label: 'We started using the analytics/reporting built into our existing tools',
			label2: '(E.g. HubSpot Analytics, GA4, Shopify, Excel, etc.)',
		},
		{
			value: BillingCancellationReason.UNRESOLVED_ISSUES,
			label: "We ran into issues with Databox which we couldn't resolve",
			label2: '(E.g. platform issues, missing features, integrations, metrics)',
		},
		{
			value: BillingCancellationReason.STOPPED_REPORTING,
			label: 'We stopped doing analytics and reporting entirely',
			label2: '(E.g. business closed down, client or project ended)',
		},
	]);

	get mainChoiceSelected(): MainChoice | null {
		return this.mainChoices.find((item) => item.value === this.mainChoiceSelectedValue) || null;
	}

	get isButtonDisabled(): boolean {
		return !this.mainChoiceSelected;
	}

	@action
	changeMainChoiceValue(value: AnySimpleValue): void {
		this.mainChoiceSelectedValue = value as number;
	}

	@action
	saveFeedback(): void {
		if (!this.mainChoiceSelected) {
			return;
		}

		const feedback = this.flowCancellationService.getFeedback() || {}; // to update feedback if there is `trial_downgrade_feedback` already filled out
		this.flowCancellationService.saveFeedback({
			...feedback,
			choice: this.mainChoiceSelected.label,
		});

		if (this.flowCancellationService.canRequestSetupHelp) {
			this.flowCancellationService.openRetentionOfferPopup(this.mainChoiceSelected.value);
		} else {
			this.flowCancellationService.openConfirmationPopup();
		}
	}
	<template>
		<div class="content">
			<CommonUiIconComponent @name="Annotation" @size="2xl" />
			<h2>We're sorry to see you go</h2>
			<p>If you have a moment, we'd love to understand what led to your decision.</p>
			<div class="feedback-choices">
				<div class="feedback-choices__content">
					<FormRadiobuttonGroupComponent
						class="radiobutton-default"
						@name="main_choices"
						@selected={{this.mainChoiceSelectedValue}}
						@options={{this.mainChoices}}
						@onChange={{this.changeMainChoiceValue}}
					/>
				</div>
			</div>
			<div class="controls2 controls2--center">
				<SubmitButtonComponent
					@text="Continue"
					@theme="red-outlined"
					@size="big"
					@noDelay={{true}}
					@isDisabled={{this.isButtonDisabled}}
					@onTrigger={{this.saveFeedback}}
				/>
				<SubmitButtonComponent
					@text="I want to keep my subscription"
					@theme="grey-empty"
					@onTrigger={{this.close}}
				/>
			</div>
		</div>
	</template>
}
